import React, { useContext, useEffect, useMemo, useState } from 'react'
import { OrderWorkflowEvent, WorkflowActionType } from 'views/order/v5/model-order'
import { SessionType, ZupplerLog } from './model'
import View from './view'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

const client = require('api/auth')

declare let RESTAURANTS_SVC_API5: string

/**
 * This will enable a new workflow action to have logs
 */
function getSessionTypeFromEventType(eventType: WorkflowActionType): SessionType | null {
  switch (eventType) {
    case 'NotifyPrinter':
    case 'NotifyPOS':
      return 'posaas_order_push'
    case 'IsExternalOrder':
      return 'external_order_worker'
    default:
      return null
  }
}

const findLog = async (sessionType: SessionType, sessionId: string, ownerId: string): Promise<ZupplerLog | null> => {
  const token = client && client.getAuthResponse() ? client.getAuthResponse().access_token : null
  if (token) {
    const res = await fetch(`${RESTAURANTS_SVC_API5}/v5/zuppler_logs`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: 1,
        per_page: 10,
        owner_id: ownerId,
        session_type: sessionType,
        session_id: sessionId,
      }),
    }).then((res) => res.json())

    if (res && res.pagination.total > 0) {
      return res.data[0]
    }

    return null
  } else {
    throw 'Invalid token to access log'
  }
}

export function ZupplerWorkflowEventLogs(props: {
  sessionId: string
  ownerId: string
  event: OrderWorkflowEvent
}): JSX.Element {
  const [active, setActive] = useState(false)
  const [logDetails, setLogDetails] = useState<ZupplerLog | null>(null)
  const enabled = useMemo(() => !!logDetails, [logDetails])
  const sessionType = useMemo(() => getSessionTypeFromEventType(props.event.type), [props.event.type])

  useEffect(() => {
    if (sessionType) {
      findLog(sessionType, props.sessionId, props.ownerId).then(setLogDetails)
    }
  }, [props.sessionId, props.event.type])

  if (!enabled) return <></>

  if (enabled) {
    return (
      <>
        <div>
          <span className="label label-info" onClick={() => setActive(true)}>
            more details
          </span>
        </div>
        {active && logDetails && (
          <ModalLog
            zupplerLog={logDetails}
            {...props}
            eventType={props.event.type}
            sessionType={sessionType as SessionType}
            onClose={() => setActive(false)}
          />
        )}
      </>
    )
  }
  return <></>
}

function ModalLog(props: {
  zupplerLog: ZupplerLog
  sessionId: string
  sessionType: SessionType
  eventType: string
  onClose: () => void
}): JSX.Element {
  return (
    <Modal isOpen={true} onClosed={props.onClose}>
      <ModalHeader>
        <span>{props.eventType} logs</span>
      </ModalHeader>
      <ModalBody>
        <View logId={props.zupplerLog.id} />
      </ModalBody>
      <ModalFooter>
        <Button type="submit" color="primary" onClick={props.onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export interface ZupplerLogsApi {
  hasLogs: () => boolean
  showLogs: () => void
  closeLogs: () => void
  getLogDetails: () => ZupplerLog | null
  shouldShowLogs: () => boolean
}

const ZupplerLogsCtx = React.createContext<ZupplerLogsApi>(null)
export function ZupplerLogsContext(
  props: React.PropsWithChildren<{ ownerType: string; ownerId: string; sessionId: string; sessionType: SessionType }>
): JSX.Element {
  const [showLogs, setShowLogs] = useState(false)
  const [logDetails, setLogDetails] = useState<ZupplerLog | null>(null)
  const api = {
    hasLogs: () => logDetails !== null,
    showLogs: () => setShowLogs(true),
    closeLogs: () => setShowLogs(false),
    shouldShowLogs: () => showLogs,
    getLogDetails: () => logDetails,
  }
  useEffect(() => {
    if (props.sessionType) {
      findLog(props.sessionType, props.sessionId, props.ownerId).then(setLogDetails)
    }
  }, [props.sessionId])
  return <ZupplerLogsCtx.Provider value={api}>{props.children}</ZupplerLogsCtx.Provider>
}

export function useZupplerLogsAPI(): ZupplerLogsApi {
  const ctx = useContext(ZupplerLogsCtx)
  return ctx as ZupplerLogsApi
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function ZupplerLogsModal() {
  const api = useZupplerLogsAPI()
  const log = api.getLogDetails()
  if (!log) return <></>
  return (
    <Modal isOpen={api.hasLogs() && api.shouldShowLogs()} onClosed={api.closeLogs}>
      <ModalHeader>
        <span>Logs</span>
      </ModalHeader>
      <ModalBody>
        <View logId={log.id} />
      </ModalBody>
      <ModalFooter>
        <Button type="submit" color="primary" onClick={api.closeLogs}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}
